// app/components/common/Checkbox/CheckBox.tsx
'use client';

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Icon } from '../Icon';
import { Typography } from '../Typography/Typography';
import { IconMap } from '../IconMap';
import { cn } from '@workspace/lib';
import { theme } from '../../theme';

const checkboxBox = cva(
  'w-5 h-5 shrink-0 flex items-center justify-center rounded-[5px] border transition-colors peer-focus:outline-2 peer-focus:outline-offset-2 peer-focus:outline-primary',
  {
    variants: {
      tone: {
        default:
          'bg-white border-secondary-300 hover:bg-warmwhite peer-checked:bg-primary peer-checked:border-transparent peer-checked:hover:bg-primary-500 ',
        error:
          'bg-error-50 border-error-300 peer-checked:bg-primary peer-checked:border-transparent peer-checked:hover:bg-primary-500',
      },
      disabled: {
        true: 'bg-secondary-100 border-secondary-300 cursor-not-allowed text-secondary-500',
        false: 'text-white',
      },
    },
    defaultVariants: { tone: 'default', disabled: false },
  },
);

type BoxVariants = VariantProps<typeof checkboxBox>;

interface CheckBoxProps {
  id?: string;
  name?: string;
  variant: 'default' | 'error';
  isChecked?: boolean;
  label?: string;
  onChange: (isChecked: boolean) => void;
  isDisabled?: boolean;
  isRequired?: boolean;
  iconName?: keyof typeof IconMap;
  iconSize?: number;
  children?: React.ReactNode;
  className?: string;
  boxClassName?: string;
  labelClassName?: string;
}

export function CheckBox({
  id,
  name,
  isChecked = false,
  variant,
  onChange,
  isDisabled = false,
  isRequired = false,
  iconName,
  iconSize = 20,
  children,
  className,
  boxClassName,
  labelClassName,
}: CheckBoxProps) {
  const tone: NonNullable<BoxVariants['tone']> = variant === 'error' ? 'error' : 'default';

  return (
    <label htmlFor={id} className={cn('group inline-flex items-center', className)}>
      <input
        id={id}
        name={name}
        type="checkbox"
        checked={isChecked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={isDisabled}
        className="peer sr-only"
      />
      <div className={cn(checkboxBox({ tone, disabled: isDisabled }), boxClassName)}>
        {isChecked && iconName && <Icon name={iconName} size={iconSize} />}
      </div>
      {children && (
        <Typography variant={theme === 'redRec' ? 'bodyMd' : 'bodySm'} className={cn('ml-xs', labelClassName)}>
          {children}
          {isRequired && (
            <span className="ml-[2px] text-error" aria-hidden="true">
              *
            </span>
          )}
        </Typography>
      )}
    </label>
  );
}
